import Component from '@glimmer/component'
import { service } from '@ember/service'
import { t } from 'ember-intl'
import WidgetCard, { WidgetContent } from 'teacher-ui-client/components/teacher-home/widget-card'
import config from 'teacher-ui-client/config/environment'
import { join } from 'teacher-ui-client/utils/uri'
import type SubscriptionTypeService from 'teacher-ui-client/services/subscription-type'
import type FeatureControlService from 'teacher-ui-client/services/feature-control'
import { SubscriptionType } from 'teacher-ui-client/subscription-type/subscription-types'
import { ButtonAppearance, appearanceToThemeClasses } from 'teacher-ui-client/components/ui-button/themes'
import PhosphorIcon from 'teacher-ui-client/components/phosphor-icon'
import { teacherEventClick } from 'teacher-ui-client/modifiers/teacher-event'
import { concat } from '@ember/helper'

interface Signature {
  Element: HTMLElement
}

interface ProgramLink {
  id: string
  labelKey: string
  path: string
  content: string
  buttonAppearance: string
}

function startProgramUrl(path: string): string {
  return join(config.readingEggsBaseUrl, path).href
}

export class TeacherHomeProgramLinks extends Component<Signature> {
  @service declare subscriptionType: SubscriptionTypeService
  @service declare featureControl: FeatureControlService

  get readingLinks(): ProgramLink[] {
    return [
      {
        id: 'reading-eggs',
        labelKey: 'products.reading-eggs',
        path: startProgramUrl('/re/teacher/class_login'),
        content: 'bg-oceany-blue-50',
        buttonAppearance: appearanceToThemeClasses(ButtonAppearance.specialBlue).join(' '),
      },
      {
        id: 'reading-eggspress',
        labelKey: 'products.reading-eggspress',
        path: startProgramUrl('/rex/teacher/class_login'),
        content: 'bg-rexy-purple-50',
        buttonAppearance: appearanceToThemeClasses(ButtonAppearance.specialPurple).join(' '),
      },
      {
        id: 'fast-phonics',
        labelKey: 'products.fast-phonics',
        path: startProgramUrl('/fast_phonics/teacher/class_login'),
        content: 'bg-pink-50',
        buttonAppearance: appearanceToThemeClasses(ButtonAppearance.specialRed).join(' '),
      },
    ]
  }

  get mathsLinks(): ProgramLink[] {
    const links: ProgramLink[] = [
      {
        id: 'mathseeds',
        labelKey: 'products.mathseeds',
        path: startProgramUrl('/ms/teacher/class_login'),
        content: 'bg-green-50',
        buttonAppearance: appearanceToThemeClasses(ButtonAppearance.specialGreen).join(' '),
      },
    ]

    if (this.featureControl.canAccess('msp_product')) {
      links.push({
        id: 'mathseeds-prime',
        labelKey: 'products.mathseeds-prime',
        path: startProgramUrl('/ms_prime/teacher/class_login'),
        content: 'bg-yellow-25',
        buttonAppearance: appearanceToThemeClasses(ButtonAppearance.specialGreen).join(' '),
      })
    }

    return links
  }

  get writingLinks(): ProgramLink[] {
    return [
      {
        id: 'writing-legends',
        labelKey: 'products.writing-legends',
        path: startProgramUrl('/wl/teacher/class_login'),
        content: 'bg-blue-100',
        buttonAppearance: appearanceToThemeClasses(ButtonAppearance.specialBlue).join(' '),
      },
    ]
  }

  get links(): ProgramLink[] {
    switch (this.subscriptionType.current) {
      case SubscriptionType.Reading:
        return this.readingLinks
      case SubscriptionType.Maths:
        return this.mathsLinks
      case SubscriptionType.Writing:
        return this.writingLinks
      default:
        return []
    }
  }

  <template>
    <WidgetCard
      @title={{t "teacherHome.widgets.programLinks.title"}}
      @borderAccentClass="border-t-rexy-purple-300"
      data-test-teacher-home-widget-card="program-links"
      ...attributes
    >
      <WidgetContent class="flex flex-col justify-center gap-2">
        {{#each this.links as |link|}}
          <a
            class="{{link.content}}
              group flex h-24 items-center justify-between rounded-3xl p-4 text-center text-neutral-300"
            data-test-program-link={{link.id}}
            href={{link.path}}
            {{teacherEventClick (concat "TeacherHome:program_link_clicked:" link.id) this.subscriptionType.current}}
          >
            <h2 class="text-lg">{{t link.labelKey}}</h2>
            <div class="{{link.buttonAppearance}} px-5 py-2">
              {{t "components.startButton.label"}}
              <PhosphorIcon @icon="phosphor-arrow-right-bold" class="ml-1" />
            </div>
          </a>
        {{/each}}
      </WidgetContent>
    </WidgetCard>
  </template>
}

export default TeacherHomeProgramLinks
